import { Dialog } from "@base-ui/react/dialog";
import { ChevronLeft, ChevronRight, X as Close } from "lucide-react";
import { useRef, useState } from "react";
import { AuctionPhotoFilmstrip } from "./auction-photo-filmstrip";

export function AuctionGallery({
  images,
  fallbackImage,
  name,
}: {
  images: readonly string[];
  fallbackImage?: string | null;
  name: string;
}) {
  const photos = [...new Set(images.filter(Boolean))];
  if (!photos.length && fallbackImage) {
    photos.push(fallbackImage);
  }
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement | null>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const count = photos.length;
  const selected = count ? ((index % count) + count) % count : 0;

  return (
    <>
      <AuctionPhotoFilmstrip
        name={name}
        onExpand={(next) => {
          setIndex(next);
          setOpen(true);
        }}
        onSelect={setIndex}
        paused={open}
        photoButtonRef={opener}
        photos={photos}
        selected={selected}
      />
      {count > 0 && (
        <Dialog.Root onOpenChange={setOpen} open={open}>
          <Dialog.Portal>
            <Dialog.Backdrop className="lockerverse-auction-gallery__backdrop" />
            <Dialog.Popup
              aria-describedby={undefined}
              className="lockerverse-auction-gallery__viewer"
              finalFocus={opener}
              onKeyDown={(event) => {
                if (
                  count < 2 ||
                  event.altKey ||
                  event.ctrlKey ||
                  event.metaKey ||
                  event.shiftKey
                ) {
                  return;
                }
                if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                  event.preventDefault();
                  setIndex(
                    (selected + (event.key === "ArrowRight" ? 1 : -1) + count) %
                      count
                  );
                }
              }}
            >
              <Dialog.Title className="lockerverse-auction-gallery__sr-only">
                {name} · Photo {selected + 1} of {count}
              </Dialog.Title>
              <div
                className="lockerverse-auction-gallery__full-image"
                onTouchCancel={() => {
                  swipeStart.current = null;
                }}
                onTouchEnd={(event) => {
                  const start = swipeStart.current;
                  swipeStart.current = null;
                  const end = event.changedTouches.item(0);
                  // biome-ignore lint/suspicious/noUnnecessaryConditions: Touch start assigns the shared ref before this event.
                  if (!(start && end) || count < 2) {
                    return;
                  }
                  const dx = end.clientX - start.x;
                  const dy = end.clientY - start.y;
                  if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
                    setIndex((selected + (dx < 0 ? 1 : -1) + count) % count);
                  }
                }}
                onTouchMove={(event) => {
                  if (event.touches.length !== 1) {
                    swipeStart.current = null;
                  }
                }}
                onTouchStart={(event) => {
                  const touch = event.touches.item(0);
                  swipeStart.current =
                    event.touches.length === 1 && touch
                      ? { x: touch.clientX, y: touch.clientY }
                      : null;
                }}
              >
                <img
                  alt={`${name}, ${selected + 1} of ${count}`}
                  height={900}
                  src={photos[selected]}
                  width={1200}
                />
                {open && count > 1 && (
                  <img
                    alt=""
                    aria-hidden="true"
                    className="lockerverse-auction-gallery__preload"
                    height={900}
                    loading="eager"
                    src={photos[(selected + 1) % count]}
                    width={1200}
                  />
                )}
              </div>
              <Dialog.Close
                aria-label="Close photo viewer"
                className="lockerverse-auction-gallery__viewer-close"
              >
                <Close aria-hidden="true" size={22} />
              </Dialog.Close>
              {count > 1 && (
                <>
                  <button
                    aria-label="Previous photo"
                    className="lockerverse-auction-gallery__viewer-previous"
                    onClick={() => setIndex((selected - 1 + count) % count)}
                    type="button"
                  >
                    <ChevronLeft aria-hidden="true" size={28} />
                  </button>
                  <button
                    aria-label="Next photo"
                    className="lockerverse-auction-gallery__viewer-next"
                    onClick={() => setIndex((selected + 1) % count)}
                    type="button"
                  >
                    <ChevronRight aria-hidden="true" size={28} />
                  </button>
                </>
              )}
            </Dialog.Popup>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </>
  );
}
